import { useEffect } from 'react';

import { Box, Skeleton } from '@chakra-ui/react';
import {
  Calendar as CalendarIcon,
  Country as CountryIcon,
  Currency as CurrencyIcon,
  DisplayName as DisplayNameIcon,
  HistoryComments as HistoryCommentsIcon,
  Icon,
  ListItemComp,
  ListItemCompAction,
  ListItemCompContent,
  ListItemCompDescription,
  ListItemCompLeading,
  ListItemCompTitle,
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
  SettingsSchedule as SettingsScheduleIcon,
} from '@mesh.ai/rabbit';
import { staticRoutes } from 'Deer/constants/routes';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { valuesFromSlack } from 'screens/admin/OrgSettings/timezone/values';
import { selectLoggedInEmployeeData } from 'selectors/loggedInEmployee';
import { checkNewNavEnabled } from 'utils/helpers/featureFlags';
import { checkGoalModuleEnabled } from 'utils/helpers/moduleAccess';

import { GOAL_MEASUREMENT_CURRENCIES } from 'constants/goalMeasurementCurrencies';
import { sendPageViewEvent } from 'constants/mixpanelHelper';
import { isNetchexLogin } from 'constants/netChexConstants';

import { updateOrganization } from 'services/admin';
import { showErrorSnackbar } from 'services/snackbar';

import { useOrganizationSettings } from './hooks/dataHooks';

const SETTINGS_FIELDS = [
  {
    heading: 'Country',
    subHeading: 'Please select your country',
    key: 'country',
    placeholderText: 'Enter country name',
    icon: CountryIcon,
    options: [
      { label: 'Australia', value: 'Australia' },
      { label: 'Brazil', value: 'Brazil' },
      { label: 'Canada', value: 'Canada' },
      { label: 'France', value: 'France' },
      { label: 'Germany', value: 'Germany' },
      { label: 'Hong Kong', value: 'Hong Kong ' },
      { label: 'India', value: 'India' },
      { label: 'Indonesia', value: 'Indonesia' },
      { label: 'Ireland', value: 'Ireland' },
      { label: 'Japan', value: 'Japan' },
      { label: 'Mexico', value: 'Mexico' },
      { label: 'Pakistan', value: 'Pakistan' },
      { label: 'Philippines', value: 'Philippines' },
      { label: 'Singapore', value: 'Singapore' },
      { label: 'UK', value: 'UK' },
      { label: 'USA', value: 'USA' },
    ],
  },
  {
    heading: 'Currency',
    subHeading: 'Please select your currency',
    key: 'currency',
    placeholderText: 'Enter your currency here',
    icon: CurrencyIcon,
    options: Object.keys(GOAL_MEASUREMENT_CURRENCIES).map((x) => ({
      label: `${GOAL_MEASUREMENT_CURRENCIES[x].name} ` + `(${GOAL_MEASUREMENT_CURRENCIES[x].symbol})`, // eslint-disable-line no-useless-concat
      value: x,
    })),
  },
  {
    heading: 'Timezone',
    subHeading: 'Please select your timezone',
    key: 'timezone',
    placeholderText: 'Please select your timezone',
    icon: SettingsScheduleIcon,
    options: valuesFromSlack,
  },
  {
    heading: 'Language',
    subHeading: 'Please select your language',
    key: 'language',
    placeholderText: 'Please select your language',
    icon: HistoryCommentsIcon,
    options: [
      { label: 'English', value: 'English' },
      { label: 'Portuguese', value: 'Portuguese' },
      { label: 'French', value: 'French' },
      { label: 'German', value: 'German' },
      { label: 'Cantonese', value: 'Cantonese' },
      { label: 'Japanese', value: 'Japanese' },
      { label: 'Spanish', value: 'Spanish' },
    ],
  },
  {
    heading: 'Start Month',
    subHeading: 'Choose start month of the first cycle (Q1, H1, Y1...)',
    key: 'startMonth',
    placeholderText: '',
    icon: CalendarIcon,
    options: [
      { label: 'January', value: 'JANUARY' },
      { label: 'February', value: 'FEBRUARY' },
      { label: 'March', value: 'MARCH' },
      { label: 'April', value: 'APRIL' },
      { label: 'May', value: 'MAY' },
      { label: 'June', value: 'JUNE' },
      { label: 'July', value: 'JULY' },
      { label: 'August', value: 'AUGUST' },
      { label: 'September', value: 'SEPTEMBER' },
      { label: 'October', value: 'OCTOBER' },
      { label: 'November', value: 'NOVEMBER' },
      { label: 'December', value: 'DECEMBER' },
    ],
  },
  {
    heading: 'Display Name',
    subHeading: 'Select display preference: Preferred name (if available) or always use first name',
    key: 'displayNameConfig',
    placeholderText: '',
    icon: DisplayNameIcon,
    options: [
      {
        label: 'Always show First name and Last name',
        value: 'FIRST_NAME_LAST_NAME',
      },
      {
        label: 'Show Preferred name and Last name if available',
        value: 'PREFERRED_NAME_LAST_NAME',
      },
    ],
  },
];

export const OrganizationSettingsIntent = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const loggedInEmployee = useSelector(selectLoggedInEmployeeData, shallowEqual);
  const organizationId = loggedInEmployee.organization.id;

  const { data: organization, isLoading, error: isError, refetch } = useOrganizationSettings(organizationId);

  const isGoalsModuleEnabled = checkGoalModuleEnabled();
  const settings = isGoalsModuleEnabled ? SETTINGS_FIELDS : SETTINGS_FIELDS.filter(({ key }) => key !== 'currency');

  // send new nav page event to mixpanel
  useEffect(() => {
    if (checkNewNavEnabled()) {
      sendPageViewEvent({
        eventProperties: {
          page: 'Company-settings-organisation details',
        },
      });
    }
  }, []);

  const handleChange = async (key, value) => {
    if (organization[key] === value) return;
    const org = { ...organization, [key]: value };
    try {
      await updateOrganization(org);
      refetch();
    } catch (err) {
      // @ts-expect-error ts issue
      dispatch(showErrorSnackbar(err?.message || 'Unable to update the settings.'));
    }
  };

  if (isLoading) {
    return (
      <Box background="#fff" padding="12px" borderRadius="8px" boxShadow="0px 0px 2px 0px rgba(0, 0, 0, 0.24)">
        <Skeleton height="20px" width="100%" marginBottom="8px" />
        <Skeleton height="20px" width="100%" marginBottom="8px" />
        <Skeleton height="20px" width="100%" marginBottom="8px" />
        <Skeleton height="20px" width="100%" marginBottom="8px" />
        <Skeleton height="20px" width="100%" marginBottom="8px" />
        <Skeleton height="20px" width="100%" />
      </Box>
    );
  }

  if (isError && !isNetchexLogin) {
    // @ts-expect-error ts issue
    dispatch(showErrorSnackbar(isError.message || 'Unable to fetch the details.'));
    history.push(staticRoutes.home);
    return null;
  }

  return (
    <Box background="#fff" padding="12px" borderRadius="8px" boxShadow="0px 0px 2px 0px rgba(0, 0, 0, 0.24)">
      {settings.map((setting, i) => (
        <ListItemComp
          style={{
            borderBottom: i === settings.length - 1 ? 'none' : '1px solid #F5F7F9',
            height: '68px',
            paddingLeft: '0',
          }}
        >
          <ListItemCompLeading style={{ color: '#666666' }}>
            <Icon IconSVG={setting.icon} size="default" />
          </ListItemCompLeading>
          <ListItemCompContent>
            <ListItemCompTitle bold={false}>{setting.heading}</ListItemCompTitle>
            <ListItemCompDescription>{setting.subHeading}</ListItemCompDescription>
          </ListItemCompContent>
          <ListItemCompAction>
            <Select onValueChange={(value) => handleChange(setting.key, value)} disabled={isLoading}>
              <SelectTrigger style={{ width: '320px', textAlign: 'left' }}>
                <SelectValue
                  placeholder={
                    setting.options.filter((opt) => opt.value === organization[setting.key])[0]?.label ||
                    setting.placeholderText
                  }
                />
              </SelectTrigger>
              <SelectContent style={{ maxHeight: '240px', padding: '0px' }}>
                <SelectGroup>
                  {setting.options.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectGroup>
              </SelectContent>
            </Select>
          </ListItemCompAction>
        </ListItemComp>
      ))}
    </Box>
  );
};
